import { mapEadinStatus } from './receivedUtils';

export const PO_STATUS_META = {
  expected: { label: 'Expected', tone: 'neutral' },
  in_transit: { label: 'In Transit', tone: 'info' },
  due_today: { label: 'Due Today', tone: 'info' },
  overdue: { label: 'Overdue', tone: 'danger' },
  receiving: { label: 'Receiving', tone: 'info' },
  partially_received: { label: 'Partial', tone: 'warn' },
  fully_received: { label: 'Received', tone: 'ok' },
  reconciled: { label: 'Reconciled', tone: 'ok' },
  blocked: { label: 'Blocked', tone: 'danger' },
  closed: { label: 'Closed', tone: 'neutral' },
};

export const BOX_STATE_META = {
  pending: { label: 'Not scanned', tone: 'neutral' },
  scanning: { label: 'Scanning', tone: 'info' },
  complete: { label: 'Complete', tone: 'ok' },
  short: { label: 'Short', tone: 'warn' },
  over: { label: 'Over', tone: 'warn' },
  duplicate_epc: { label: 'Duplicate EPC', tone: 'danger' },
  hand_count_pending: { label: 'Hand count', tone: 'warn' },
  override_received: { label: 'Override recv.', tone: 'ok' },
};

export const TONE_BADGE = {
  neutral: 'bg-gray-100 text-gray-600',
  ok: 'bg-green-100 text-green-800',
  warn: 'bg-amber-100 text-amber-800',
  danger: 'bg-red-100 text-red-800',
  info: 'bg-blue-100 text-blue-800',
};

export const TONE_TEXT = {
  neutral: 'text-gray-600',
  ok: 'text-green-700',
  warn: 'text-amber-700',
  danger: 'text-red-700',
  info: 'text-blue-700',
};

function todayDenver() {
  return new Date().toLocaleDateString('en-CA', { timeZone: 'America/Denver' });
}

function dateOnly(iso) {
  if (!iso) return null;
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return String(iso).slice(0, 10);
  return d.toLocaleDateString('en-CA', { timeZone: 'America/Denver' });
}

export function totalExpected(po) {
  const boxes = po.boxes || [];
  if (boxes.length > 0) {
    return boxes.reduce((s, b) => s + (b.expectedUnits || 0), 0);
  }
  return (po.lineItems || []).reduce((s, li) => s + (li.expectedUnits || 0), 0);
}

export function totalReceived(po) {
  const boxes = po.boxes || [];
  if (boxes.length > 0) {
    return boxes.reduce((s, b) => s + boxReceivedUnits(b), 0);
  }
  return (po.lineItems || []).reduce((s, li) => s + (li.receivedUnits || 0), 0);
}

/** Inbound received − expected (matches Expected / Received columns). */
export function unitVariance(po) {
  return totalReceived(po) - totalExpected(po);
}

/** Effective received units for a box (amend > hand > inbound received). */
export function boxReceivedUnits(box) {
  if (!box) return 0;
  return box.amendedUnits ?? box.handCountUnits ?? box.receivedUnits ?? 0;
}

/** Sum inbound received units per SKU across all boxes/containers. */
export function rollupReceivedBySku(boxes = []) {
  const bySku = new Map();
  for (const box of boxes) {
    if (!box?.sku) continue;
    bySku.set(box.sku, (bySku.get(box.sku) || 0) + boxReceivedUnits(box));
  }
  return bySku;
}

/** Sync PO line-item received totals from box/container inbound received. */
export function syncLineItemsReceivedFromBoxes(po) {
  if (!po?.lineItems?.length) return po;
  const bySku = rollupReceivedBySku(po.boxes || []);
  return {
    ...po,
    lineItems: po.lineItems.map((li) => ({
      ...li,
      receivedUnits: bySku.has(li.sku) ? bySku.get(li.sku) : (li.receivedUnits || 0),
    })),
  };
}

export function isBoxReceived(box) {
  return boxReceivedUnits(box) > 0;
}

/** Boxes progress: received boxes / total boxes (not scan state). */
export function boxesResolved(po) {
  const boxes = po.boxes || [];
  return {
    resolved: boxes.filter(isBoxReceived).length,
    total: boxes.length,
  };
}

/** Scan/hand variance vs expected — not the same as inbound unit variance. */
export function scanVariance(po) {
  const boxes = po.boxes || [];
  const counted = boxes.filter(
    (b) => b.scannedUnits !== null || b.amendedUnits != null || b.handCountUnits !== null,
  );
  if (counted.length === 0) return null;
  const scannedTotal = boxes.reduce(
    (s, b) => s + (b.amendedUnits ?? b.handCountUnits ?? b.scannedUnits ?? 0),
    0,
  );
  const expectedTotal = boxes.reduce((s, b) => s + b.expectedUnits, 0);
  return scannedTotal - expectedTotal;
}

export function hasOpenException(po) {
  return (po.exceptions || []).some((e) => !e.resolved);
}

export function needsHandCount(po) {
  return (po.boxes || []).some((b) =>
    ['short', 'over', 'duplicate_epc', 'hand_count_pending'].includes(b.state),
  );
}

export function isPartiallyReceived(po) {
  return po.status === 'partially_received' || po.status === 'receiving';
}

export function awaitingReconciliation(po) {
  return po.status === 'fully_received';
}

export function readyToClose(po) {
  return po.status === 'reconciled';
}

export function isDueToday(po) {
  const today = todayDenver();
  return (
    dateOnly(po.expectedDate) === today &&
    !['closed', 'reconciled', 'fully_received'].includes(po.status) &&
    mapEadinStatus(po.eadinStatus) !== 'closed'
  );
}

export function isOverdue(po) {
  const today = todayDenver();
  const due = dateOnly(po.expectedDate);
  return (
    due &&
    due < today &&
    !po.receivedDate &&
    !['closed', 'reconciled', 'fully_received'].includes(po.status) &&
    mapEadinStatus(po.eadinStatus) !== 'closed'
  );
}

export function isDueThisWeek(po) {
  const today = todayDenver();
  const due = dateOnly(po.expectedDate);
  if (!due || due <= today) return false;
  const dueMs = new Date(`${due}T00:00:00`).getTime();
  const todayMs = new Date(`${today}T00:00:00`).getTime();
  const diff = Math.round((dueMs - todayMs) / 86400000);
  return diff > 0 && diff <= 7 && !['closed', 'reconciled', 'fully_received'].includes(po.status);
}

export function fmtDate(iso) {
  if (!iso) return '—';
  const d = new Date(iso.length === 10 ? `${iso}T00:00:00` : iso);
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
}

export function fmtDateTime(iso) {
  if (!iso) return '—';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '—';
  return d.toLocaleString('en-US', {
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
    hour12: true,
  });
}

export function relDue(po) {
  if (po.receivedDate) return { text: fmtDate(po.receivedDate), tone: 'neutral' };
  const today = todayDenver();
  const due = dateOnly(po.expectedDate);
  if (!due) return { text: '—', tone: 'neutral' };
  const diff = Math.round(
    (new Date(`${due}T00:00:00`).getTime() - new Date(`${today}T00:00:00`).getTime()) / 86400000,
  );
  if (diff < 0) return { text: `${Math.abs(diff)}d late`, tone: 'danger' };
  if (diff === 0) return { text: 'Today', tone: 'info' };
  if (diff === 1) return { text: 'Tomorrow', tone: 'neutral' };
  return { text: `In ${diff}d`, tone: 'neutral' };
}

export function derivePoStatus(po) {
  if (po.status === 'closed' || mapEadinStatus(po.eadinStatus) === 'closed') return 'closed';
  const boxes = po.boxes || [];
  const resolved = boxes.filter((b) => b.state === 'complete' || b.state === 'override_received');
  const hasBlocking = (po.exceptions || []).some(
    (e) => !e.resolved && (e.kind === 'duplicate_epc' || e.kind === 'missing_docs'),
  );
  if (hasBlocking && resolved.length < boxes.length) return 'blocked';
  if (boxes.length > 0 && resolved.length === boxes.length) {
    return boxes.some((b) => b.state === 'override_received') ? 'fully_received' : 'reconciled';
  }
  if (resolved.length > 0) return 'partially_received';
  const expected = totalExpected(po);
  const received = totalReceived(po);
  if (received > 0) return 'receiving';
  if (isOverdue(po)) return 'overdue';
  if (isDueToday(po)) return 'due_today';
  return po.status || 'expected';
}

export const RECEIVING_STATUS_META = {
  not_started: { label: 'Not started', tone: 'neutral' },
  in_transit: { label: 'In transit', tone: 'info' },
  receiving: { label: 'Receiving', tone: 'info' },
  received: { label: 'Received', tone: 'ok' },
  short: { label: 'Short', tone: 'warn' },
};

/** Box-level receiving progress collapsed to a single lifecycle status. */
export function deriveReceivingStatus(po) {
  const boxes = po.boxes || [];
  const { resolved, total } = boxesResolved(po);
  const shortBoxes = boxes.filter(
    (b) => b.state === 'short' || b.state === 'over' || b.state === 'hand_count_pending',
  ).length;
  if (total > 0 && resolved + shortBoxes === total && shortBoxes > 0) return 'short';
  if (total > 0 && resolved === total) return 'received';
  const expected = totalExpected(po);
  const received = totalReceived(po);
  if (expected > 0 && received >= expected) return 'received';
  if (received > 0 || resolved > 0 || shortBoxes > 0) return 'receiving';
  if (po.status === 'in_transit' || isOverdue(po) || isDueToday(po)) return 'in_transit';
  return 'not_started';
}

export const TRIAGE = [
  {
    key: 'due_today',
    label: 'Due today',
    match: isDueToday,
    tone: 'info',
    info: 'POs whose expected arrival date is today and are not yet fully received.',
  },
  {
    key: 'due_week',
    label: 'Due this week',
    match: isDueThisWeek,
    tone: 'neutral',
    info: 'POs due within the next 7 days.',
  },
  {
    key: 'overdue',
    label: 'Overdue',
    match: isOverdue,
    tone: 'danger',
    info: 'POs past their due date that still are not fully received.',
  },
  {
    key: 'partial',
    label: 'Partial',
    match: isPartiallyReceived,
    tone: 'warn',
    info: 'Some boxes received, but not all.',
  },
  {
    key: 'hand_count',
    label: 'Needs hand count',
    match: needsHandCount,
    tone: 'warn',
    info: 'A box scanned short/over and needs a physical hand count.',
  },
  {
    key: 'reconcile',
    label: 'To reconcile',
    match: awaitingReconciliation,
    tone: 'info',
    info: 'All boxes in but totals need reconciliation before close.',
  },
  {
    key: 'ready_close',
    label: 'Ready to close',
    match: readyToClose,
    tone: 'ok',
    info: 'Every box received and reconciled — ready to close.',
  },
  {
    key: 'exceptions',
    label: 'Exceptions',
    match: hasOpenException,
    tone: 'danger',
    info: 'POs with at least one unresolved exception.',
  },
];
